import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'core-system/Modal'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import FlexContainer from 'core-system/FlexContainer'
import Text from 'core-system/Text'
import pxToRem from 'core-system/utils/pxToRem'
import moment from 'moment'
import ReceiptPreview from './ReceiptPreview'
import { confirmReceiptDetails } from 'employee-platform/features/Reimbursement/services'
import { confirmReceiptDetailsForEmployee } from '../../adminReceiptServices'
import {
  buildNewManualProcessedReceipt,
  formatMerchantCurrencyAmount,
  hasValidProcessedReceipts,
  isProcessedReceiptValid,
} from '../../utils'
import EditIcon from 'core-system/Icons/Actions/Edit'
import palette from 'core-system/Themes/palette'
import InlineReceiptEditor from './InlineReceiptEditor'

export interface ProcessedReceipt {
  receiptId: string
  merchant: string
  amount: number
  receiptDate: string
  expenseType: string | null
  merchantCategory: string | null
  merchantCurrency: string
  merchantAmount: number
  employeeNote?: string | null
  manualReview: boolean
  error: string | null
}

interface ReceiptReviewModalProps {
  open: boolean
  onClose: () => void
  receipts: ProcessedReceipt[]
  variant: 'employee' | 'admin'
  employeeId?: string
  downloadUrl: string
  contentType: string
  onSubmitSuccess?: () => void
  onSubmitError?: () => void
}

const ReceiptRow = styled(FlexContainer)`
  padding: 0.5rem 0.75rem;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => props.theme.palette.primary.pink200};
  }
`

const EditButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  margin-left: 1rem;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => props.theme.palette.grey.grey200};
  }
`

const ReceiptCard = styled.div<{ selected: boolean; invalid: boolean }>`
  border-radius: 12px;
  background-color: ${palette.white};
  border: ${({ selected, invalid, theme }) => {
    if (selected) return `2px solid ${theme.palette.secondary.purple600}`
    if (invalid) return `1px solid ${theme.palette.error.red400}`
    return `1px solid ${theme.palette.grey.grey200}`
  }};
  box-shadow: none;
  margin-bottom: 0.5rem;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
`

const AddReceiptButton = styled.button`
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 999px;
  border: 1px dashed ${palette.primary.pink500};
  background-color: ${palette.white};
  color: ${palette.primary.pink700};
  cursor: pointer;
  ${(props) => props.theme.typography.action4};

  &:hover {
    background-color: ${palette.primary.pink50};
  }
`

const Collapsible = styled.div<{ expanded: boolean }>`
  overflow: hidden;
  max-height: ${({ expanded }) => (expanded ? '380px' : '0')};
  opacity: ${({ expanded }) => (expanded ? 1 : 0)};
  transition:
    max-height 200ms ease,
    opacity 200ms ease;
  margin-top: ${({ expanded }) => (expanded ? '0.5rem' : '0')};
  border-radius: 8px;
  border: 1px solid transparent;

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    /* On smaller screens, still collapse when not expanded */
    overflow: ${({ expanded }) => (expanded ? 'visible' : 'hidden')};
    max-height: ${({ expanded }) => (expanded ? 'none' : '0')};
  }
`

const REVIEW_DESCRIPTION =
  "Thank you for uploading your receipt. We've detected the following transaction(s) from your upload. If any of the details look incorrect, please update, delete, or add claims before submitting."

const ReceiptReviewModal = React.memo(
  ({
    open,
    onClose,
    receipts,
    variant,
    employeeId,
    downloadUrl,
    contentType,
    onSubmitSuccess,
    onSubmitError,
  }: ReceiptReviewModalProps) => {
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
    const [editableReceipts, setEditableReceipts] =
      useState<ProcessedReceipt[]>(receipts)
    const [submitting, setSubmitting] = useState(false)

    const hasInitialReceipts = editableReceipts.length > 0

    useEffect(() => {
      setEditableReceipts(receipts)
      setSelectedIndex(null)
    }, [receipts])

    const handleRowClick = (index: number) => {
      setSelectedIndex((current) => (current === index ? null : index))
    }

    const handleSaveReceipt = (updatedReceipt: ProcessedReceipt) => {
      if (selectedIndex !== null) {
        const updated = [...editableReceipts]
        updated[selectedIndex] = updatedReceipt
        setEditableReceipts(updated)
      }
      setSelectedIndex(null)
    }

    const handleDeleteReceipt = (index: number) => {
      const updated = editableReceipts.filter((_, i) => i !== index)
      setEditableReceipts(updated)
      setSelectedIndex(null)
    }

    const handleSubmit = async () => {
      setSubmitting(true)
      try {
        if (variant === 'employee') {
          await confirmReceiptDetails(downloadUrl, editableReceipts)
        } else if (variant === 'admin' && employeeId) {
          await confirmReceiptDetailsForEmployee(
            downloadUrl,
            employeeId,
            editableReceipts
          )
        }
        onSubmitSuccess?.()
      } catch (error) {
        console.error('Failed to confirm receipt details:', error)
        onSubmitError?.()
      } finally {
        onClose()
        setSubmitting(false)
      }
    }

    const handleAddReceipt = () => {
      const newReceipt = buildNewManualProcessedReceipt(editableReceipts.length)

      const updated = [...editableReceipts, newReceipt]
      setEditableReceipts(updated)
      setSelectedIndex(updated.length - 1)
    }

    const isSubmitDisabled =
      submitting ||
      (variant === 'admin' && !employeeId) ||
      !hasValidProcessedReceipts(editableReceipts)

    return (
      <>
        <Modal open={open} onClose={onClose} width={pxToRem(750)}>
          <ModalHeader title='Review Your Claims' />
          <ModalBody padding='1.5rem 2rem'>
            <Text variant='body1' marginBottom='1rem'>
              {REVIEW_DESCRIPTION}
            </Text>
            <ReceiptPreview
              open={open}
              downloadUrl={downloadUrl}
              contentType={contentType}
            />
            {!hasInitialReceipts ? (
              <Text variant='body1' color='text.secondary'>
                No claims detected for this receipt.
              </Text>
            ) : (
              <>
                {editableReceipts.map((receipt, index) => {
                  const isValid = isProcessedReceiptValid(receipt)
                  return (
                    <ReceiptCard
                      key={receipt.receiptId}
                      selected={selectedIndex === index}
                      invalid={!isValid}
                    >
                      <ReceiptRow
                        justifyContent='space-between'
                        alignItems='center'
                        onClick={() => handleRowClick(index)}
                      >
                        <div>
                          <Text variant='body2' marginBottom='0.25rem'>
                            {moment(receipt.receiptDate).format(
                              'MMM D, YYYY · h:mm A'
                            )}
                          </Text>
                          <Text variant='action4'>{receipt.merchant}</Text>
                        </div>
                        <FlexContainer alignItems='center'>
                          <Text variant='action3'>
                            {formatMerchantCurrencyAmount(
                              receipt.merchantCurrency,
                              receipt.merchantAmount
                            )}
                          </Text>
                          <EditButton
                            onClick={(e) => {
                              e.stopPropagation()
                              handleRowClick(index)
                            }}
                          >
                            <EditIcon
                              color={palette.text.secondary}
                              height={16}
                              width={16}
                            />
                          </EditButton>
                        </FlexContainer>
                      </ReceiptRow>
                      {!isValid && (
                        <Text
                          variant='body2'
                          color='error'
                          marginLeft='0.5rem'
                          marginBottom='0.25rem'
                        >
                          Please complete all details for this claim.
                        </Text>
                      )}
                      <Collapsible expanded={selectedIndex === index}>
                        <InlineReceiptEditor
                          receipt={receipt}
                          onSave={handleSaveReceipt}
                          onRemove={() => handleDeleteReceipt(index)}
                        />
                      </Collapsible>
                    </ReceiptCard>
                  )
                })}
              </>
            )}
            <AddReceiptButton type='button' onClick={handleAddReceipt}>
              + Add another claim
            </AddReceiptButton>
          </ModalBody>
          <ModalFooter>
            <SecondaryButton onClick={onClose} disabled={submitting}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              style={{ marginLeft: '1rem' }}
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </PrimaryButton>
          </ModalFooter>
        </Modal>
      </>
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  ReceiptReviewModal.displayName = 'ReceiptReviewModal'
}

export default ReceiptReviewModal
